import { Box, Card, CardContent, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { PersonOutline, Description, ArrowForward } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useGetStudentsQuery } from "../../services/students.service";
import { useGetSubmissionsQuery } from "../../services/submissions.service";
import { useAppTheme } from "../../context/ThemeContext";
import moment from "moment";

type FeedItem = {
  id: string;
  type: "student" | "submission";
  title: string;
  badge: string;
  color: string;
  ts: number;
  link?: string;
};

const STUDENT_COLORS: Record<string, string> = {
  "completed successfully": "#059669",
  "visa:submitted": "#059669",
  "admission:submitted": "#059669",
  "commission:pending": "#F59E0B",
  "issue resolution": "#EF4444",
  "to be archived": "#94A3B8",
  "cooperation done": "#10B981",
};

const SUBMISSION_COLORS: Record<string, string> = {
  accepted: "#059669",
  completed: "#10B981",
  rejected: "#EF4444",
  pending: "#EAB308",
  registering: "#0EA5E9",
};

function ActivityFeedWidget() {
  const { data: students } = useGetStudentsQuery("Students");
  const { data: submissions } = useGetSubmissionsQuery("Submissions");
  const { isNewTheme, accentColor } = useAppTheme();
  const navigate = useNavigate();

  const items: FeedItem[] = [];

  (students ?? []).forEach((s) => {
    const ts = s.updatedAt ? new Date(s.updatedAt as any).getTime() : 0;
    if (!ts) return;
    items.push({
      id: `s-${s._id}`,
      type: "student",
      title: s.fullName,
      badge: s.status,
      color: STUDENT_COLORS[s.status] ?? "#64748B",
      ts,
      link: `/students/${s._id}`,
    });
  });

  (submissions ?? []).forEach((s) => {
    const ts = s.updatedAt ? new Date(s.updatedAt as any).getTime() : 0;
    if (!ts) return;
    items.push({
      id: `sub-${s._id}`,
      type: "submission",
      title: s.fullName,
      badge: s.status as string,
      color: SUBMISSION_COLORS[s.status as string] ?? "#64748B",
      ts,
    });
  });

  items.sort((a, b) => b.ts - a.ts);
  const feed = items.slice(0, 20);

  const cardSx = isNewTheme
    ? {
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
        display: "flex",
        flexDirection: "column" as const,
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
      }
    : {
        width: "100%",
        borderRadius: "10px",
        boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
        display: "flex",
        flexDirection: "column" as const,
      };

  return (
    <Card sx={cardSx}>
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Box>
          <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
            Activity Feed
          </Typography>
          {isNewTheme && (
            <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {feed.length} recent events
          </Typography>
          <Link to="/students" style={{ textDecoration: "none" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "primary.main", fontSize: "0.75rem", fontWeight: 600, opacity: 0.85, "&:hover": { opacity: 1 } }}>
              View all
              <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ flex: 1, overflow: "auto", p: 1.5, "&:last-child": { pb: 1.5 } }}>
        {feed.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", mt: 3, fontStyle: "italic" }}>
            No recent activity
          </Typography>
        ) : (
          <Stack
            divider={<Divider sx={isNewTheme ? { borderColor: "#F1F5F9" } : {}} />}
          >
            {feed.map((item) => (
              <Tooltip key={item.id} title={item.link ? `Click to view profile` : ""} placement="left" arrow>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  p={1}
                  onClick={item.link ? () => navigate(item.link!) : undefined}
                  sx={
                    isNewTheme
                      ? {
                          borderRadius: "8px",
                          cursor: item.link ? "pointer" : "default",
                          transition: "background 0.15s ease",
                          "&:hover": item.link ? { bgcolor: "#F8FAFC" } : {},
                        }
                      : { cursor: item.link ? "pointer" : "default" }
                  }
                >
                  {/* Type icon dot */}
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: item.color,
                      flexShrink: 0,
                    }}
                  />

                  {/* Name + badge */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={isNewTheme ? 600 : 400}
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {item.title}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
                      {item.type === "submission" && (
                        <Description sx={{ fontSize: 10, color: "#94A3B8" }} />
                      )}
                      {item.type === "student" && (
                        <PersonOutline sx={{ fontSize: 10, color: "#94A3B8" }} />
                      )}
                      <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "0.62rem" }}>
                        {item.type === "student" ? "student" : "submission"}
                      </Typography>
                      <Chip
                        label={item.badge.replace(":", " ")}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          borderRadius: "4px",
                          fontSize: "0.58rem",
                          height: 16,
                          bgcolor: item.color + "20",
                          color: item.color,
                          border: `1px solid ${item.color}40`,
                        }}
                      />
                    </Stack>
                  </Box>

                  {/* Timestamp */}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ flexShrink: 0, fontSize: "0.65rem" }}
                  >
                    {moment(item.ts).fromNow()}
                  </Typography>
                </Stack>
              </Tooltip>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default ActivityFeedWidget;
